/*
 * Назначение: вкладка «Общие» в настройках проекта.
 * Функционал: имя, домен, описание, приветствие, тон, retention, целевые
 * действия, быстрые кнопки, активность. Клонирование и опасная зона —
 * отдельные блоки в той же вкладке.
 */

import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import type { ProjectDetails, Tone } from "../../../lib/types";

import { CloneProject } from "./clone-project";
import { DangerZone } from "./danger-zone";
import { splitLines, useProjectMutation } from "./shared";

export function GeneralTab({ project }: { project: ProjectDetails }) {
  const mutation = useProjectMutation("сохранить общие настройки");
  const [form, setForm] = useState({
    name: project.name,
    description: project.description ?? "",
    domain: project.domain ?? "",
    is_active: project.is_active,
    greeting_text: project.greeting_text ?? "",
    tone: project.tone,
    target_actions: (project.target_actions ?? []).join("\n"),
    quick_replies: (project.quick_replies ?? []).join("\n"),
    history_retention_days: project.history_retention_days,
  });

  useEffect(() => {
    setForm({
      name: project.name,
      description: project.description ?? "",
      domain: project.domain ?? "",
      is_active: project.is_active,
      greeting_text: project.greeting_text ?? "",
      tone: project.tone,
      target_actions: (project.target_actions ?? []).join("\n"),
      quick_replies: (project.quick_replies ?? []).join("\n"),
      history_retention_days: project.history_retention_days,
    });
  }, [project]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Общие настройки</CardTitle>
          <CardDescription>Базовые параметры проекта и тон общения.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="g-name">Название в админке</Label>
              <Input
                id="g-name"
                value={form.name}
                maxLength={128}
                placeholder="Например: Металл — aimetalbot.ru"
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Видно только вам: список проектов, заявки, письма менеджерам.
                Посетитель сайта видит «Название в окне чата» — вкладка
                «Внешний вид».
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="g-domain">Домен сайта</Label>
              <Input
                id="g-domain"
                placeholder="example.com"
                value={form.domain}
                onChange={(e) => setForm((s) => ({ ...s, domain: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="g-desc">Краткое описание</Label>
            <Textarea
              id="g-desc"
              rows={3}
              value={form.description}
              onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="g-greet">Приветственное сообщение</Label>
            <Textarea
              id="g-greet"
              rows={3}
              value={form.greeting_text}
              onChange={(e) => setForm((s) => ({ ...s, greeting_text: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="g-tone">Тон общения</Label>
              <Select
                value={form.tone}
                onValueChange={(v) => setForm((s) => ({ ...s, tone: v as Tone }))}
              >
                <SelectTrigger id="g-tone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="friendly">Дружелюбный</SelectItem>
                  <SelectItem value="business">Деловой</SelectItem>
                  <SelectItem value="expert">Экспертный</SelectItem>
                  <SelectItem value="custom">Свой (см. вкладку «Промпт»)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="g-retention">
                Срок хранения диалогов, дней (1–365)
              </Label>
              <Input
                id="g-retention"
                type="number"
                min={1}
                max={365}
                value={form.history_retention_days}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    history_retention_days: Math.max(
                      1,
                      Math.min(365, Number(e.target.value) || 30),
                    ),
                  }))
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="g-targets">Целевые действия (по одному в строке)</Label>
              <Textarea
                id="g-targets"
                rows={3}
                value={form.target_actions}
                onChange={(e) => setForm((s) => ({ ...s, target_actions: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="g-quick">Быстрые кнопки виджета (по одной в строке)</Label>
              <Textarea
                id="g-quick"
                rows={3}
                value={form.quick_replies}
                onChange={(e) => setForm((s) => ({ ...s, quick_replies: e.target.value }))}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="g-active"
              checked={form.is_active}
              onCheckedChange={(v) => setForm((s) => ({ ...s, is_active: Boolean(v) }))}
            />
            <Label htmlFor="g-active">Проект активен</Label>
          </div>

          <Button
            onClick={() =>
              mutation.mutate({
                name: form.name.trim() || project.name,
                description: form.description || null,
                domain: form.domain || null,
                is_active: form.is_active,
                greeting_text: form.greeting_text || null,
                tone: form.tone,
                target_actions: splitLines(form.target_actions),
                quick_replies: splitLines(form.quick_replies),
                history_retention_days: form.history_retention_days,
              })
            }
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Сохранить
          </Button>
        </CardContent>
      </Card>

      <CloneProject project={project} />
      <VerifyToggleCard project={project} />
      <DangerZone project={project} />
    </div>
  );
}

/**
 * Карточка переключателя верификации телефона по СМС.
 * Включается отдельно — это платный модуль (СМС стоят денег), и не на
 * каждом проекте это нужно. Когда выключен — поведение прежнее: контакт
 * принимается без подтверждения.
 */
function VerifyToggleCard({ project }: { project: ProjectDetails }) {
  const mutation = useProjectMutation("сохранить настройку верификации");
  const [enabled, setEnabled] = useState(project.verify_enabled);

  useEffect(() => {
    setEnabled(project.verify_enabled);
  }, [project.verify_enabled]);

  const onChange = (v: boolean) => {
    setEnabled(v);
    mutation.mutate({ verify_enabled: v });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Верификация телефона по СМС</CardTitle>
        <CardDescription>
          Если включено — после того как клиент оставил телефон, ИИ просит
          ввести 4-значный код из СМС. Заявка создаётся только после
          подтверждения. Используется отдельный сервис на поддомене
          verify.ai19god.ru — там настраиваются доступ к TargetSMS,
          антифрод-лимиты и журнал.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3">
          <Switch
            id="verify-enabled"
            checked={enabled}
            onCheckedChange={onChange}
            disabled={mutation.isPending}
          />
          <Label htmlFor="verify-enabled">
            {enabled ? "Включена" : "Выключена"}
          </Label>
          {mutation.isPending && (
            <Loader2 className="ml-2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Если СМС-сервис недоступен или превышен лимит — заявка всё равно
          создастся (без подтверждения), чтобы не терять клиента.
        </p>
      </CardContent>
    </Card>
  );
}
